import { updateUserActivated, upsertUsers } from '@rodeo-dev/team-chat'
import { SUPPORT_ACCOUNTS, UI_AVATARS_API_URL } from '../../../../shared/constants'
import app from '../../../app'
import { RODEO_URL, USER_ROLES, USER_STATUS_ACTIVE } from '../../../lib/constants'
import { logger } from '../../../lib/logging'
import { STREAM_ADMIN_ROLE, STREAM_USER_ROLE } from './constants'

const isChatEnabled = () => {
  const featureFlagEnabled =
    app.getFeatureToggles().FEATURE_USE_STREAM === 'true'
  const isSupportUser = app.isSupportUser()
  return featureFlagEnabled && !isSupportUser
}

/**
 * Converts a web app user object to a Stream compatible user object.
 * @param {object} user
 * @returns {object}
 */
const convertToStreamUser = (user) => {
  const { userId, firstName, lastName, imageUrl, roles } = user

  // clear avatars.com urls as stream creates it's own fallback
  const hasImageUrl =
    imageUrl && imageUrl.length && !imageUrl.includes(UI_AVATARS_API_URL)
  const image = hasImageUrl ? `${RODEO_URL}${imageUrl}` : ''

  const role = roles.some((role) => USER_ROLES.includes(role))
    ? STREAM_ADMIN_ROLE
    : STREAM_USER_ROLE

  return {
    id: userId,
    username: userId,
    name: `${firstName} ${lastName}`,
    first_name: firstName,
    last_name: lastName,
    role,
    image
  }
}

/**
 * @param {string} teamId
 * @returns {Promise<[]>}
 */
const getUsersForStream = async (teamId) => {
  try {
    const params = {
      query: {
        teamId,
        status: USER_STATUS_ACTIVE
      }
    }

    const { data: users } = await app.service('/api/users/team').find(params)

    const excludeSupportAccounts = ({ email }) =>
      !Object.values(SUPPORT_ACCOUNTS).includes(email)

    return users.filter(excludeSupportAccounts).map(convertToStreamUser)
  } catch (err) {
    logger.error('Attempt to fetch users from Stream chat failed!', err)
  }
}

/**
 * @param {object} user
 * @param {string} teamId
 * @param {function} [callback]
 * @returns {Promise<void>}
 */
const upsertUserInStream = async (user, teamId, callback) => {
  if (!isChatEnabled()) return

  const { baseURL } = app.getConfig().chat
  const streamUser = convertToStreamUser(user)
  const authToken = `Bearer ${window.localStorage.getItem(
    app.getJwtStorageKey()
  )}`

  try {
    await upsertUsers(baseURL, authToken, [streamUser], teamId)
  } catch (err) {
    logger.error('Attempt to upsert users has failed!', err)
    throw err
  }

  typeof callback === 'function' && callback()
}
/**
 * @param {string} userID
 * @param {boolean} activated
 * @returns {Promise<void>}
 */
const updateStreamUserActivated = async (userID, activated) => {
  if (!isChatEnabled()) return

  const { baseURL } = app.getConfig().chat
  const authToken = `Bearer ${window.localStorage.getItem(
    app.getJwtStorageKey()
  )}`

  try {
    return updateUserActivated(baseURL, authToken, userID, activated)
  } catch (err) {
    logger.error(`Attempt to ${activated ? 'activate' : 'deactivate'} user has failed!`, err)
    throw err
  }
}

export {
  isChatEnabled,
  getUsersForStream,
  upsertUserInStream,
  convertToStreamUser,
  updateStreamUserActivated
}
